import { FC, useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { MatrixGrid } from "./MatrixGrid";
import { CompareCellContainer } from "./CompareCellContainer";
import { MatrixMessage } from "./MatrixMessage";
import { ThresholdControls } from "./ThresholdControls";
import type { MatrixThresholds } from "@/lib/scoring/matrix/bands";
import {
  profileScoringAxes,
  transformFilterScoring,
} from "@/lib/scoring/scoringUtils";
import type { ScoringProfile } from "@/lib/scoring/types";
import type { OpportunityGeneratorFilter } from "@/generated/company-search";

interface ScoringCompareMatrixProps {
  profileA: ScoringProfile | null;
  profileB: ScoringProfile | null;
  colorA: string;
  colorB: string;
  /** Active profiles eligible to be compared against A (i.e. excluding A). */
  compareCandidates: ScoringProfile[];
  onCompareProfileChange: (id: number) => void;
  baseFilters?: OpportunityGeneratorFilter;
  localizeInstalls: boolean;
  thresholds: MatrixThresholds;
  onThresholdsChange: (t: MatrixThresholds) => void;
}

const LegendDot: FC<{ color: string }> = ({ color }) => (
  <Box
    sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }}
    component="span"
  />
);

export const ScoringCompareMatrix: FC<ScoringCompareMatrixProps> = ({
  profileA,
  profileB,
  colorA,
  colorB,
  compareCandidates,
  onCompareProfileChange,
  baseFilters,
  localizeInstalls,
  thresholds,
  onThresholdsChange,
}) => {
  const scoringProfilesA = useMemo(
    () => (profileA ? transformFilterScoring([profileA]) : []),
    [profileA]
  );
  const scoringProfilesB = useMemo(
    () => (profileB ? transformFilterScoring([profileB]) : []),
    [profileB]
  );

  const [loadingCells, setLoadingCells] = useState<Record<string, boolean>>({});
  const handleCellLoading = useCallback((key: string, loading: boolean) => {
    setLoadingCells((prev) =>
      prev[key] === loading ? prev : { ...prev, [key]: loading }
    );
  }, []);
  const anyLoading = Object.values(loadingCells).some(Boolean);

  if (!profileA || !profileB) {
    return (
      <MatrixMessage>Apply two scoring profiles to compare them.</MatrixMessage>
    );
  }

  // Both profiles must score on fit and intent to build the comparison matrix.
  const incomplete = [profileA, profileB].find((p) => {
    const axes = profileScoringAxes(p);
    return !axes.hasFit || !axes.hasIntent;
  });
  if (incomplete) {
    return (
      <MatrixMessage>
        <strong>{incomplete.name}</strong> doesn’t score on both Fit and Intent.
        Compare needs two profiles that each score on both axes.
      </MatrixMessage>
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={{ height: "100%", minHeight: 0, maxWidth: 1180 }}
      data-testid="scoring-compare-matrix"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Comparing:
          </Typography>
          <LegendDot color={colorA} />
          <Typography variant="body2" fontWeight={600}>
            {profileA.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            vs
          </Typography>
          <LegendDot color={colorB} />
          {compareCandidates.length > 1 ? (
            <Select
              value={profileB.id}
              onChange={(e) => onCompareProfileChange(Number(e.target.value))}
              variant="standard"
              data-testid="compare-profile-select"
              sx={{ fontSize: "0.875rem", fontWeight: 600 }}
            >
              {compareCandidates.map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ fontSize: "0.875rem" }}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Typography variant="body2" fontWeight={600}>
              {profileB.name}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {anyLoading && <CircularProgress size={14} thickness={5} />}
          <Typography variant="caption" color="text.secondary">
            {anyLoading ? "Calculating…" : "Delta = A − B"}
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <MatrixGrid
          renderCell={(fit, intent) => (
            <CompareCellContainer
              baseFilters={baseFilters}
              scoringProfilesA={scoringProfilesA}
              scoringProfilesB={scoringProfilesB}
              localizeInstalls={localizeInstalls}
              fit={fit}
              intent={intent}
              thresholds={thresholds}
              onLoadingChange={(loading) =>
                handleCellLoading(`${fit}-${intent}`, loading)
              }
            />
          )}
        />
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <ThresholdControls value={thresholds} onChange={onThresholdsChange} />
      </Box>
    </Stack>
  );
};
